<template>
  <div class="flex flex-col flex-[8] rounded-lg overflow-hidden bg-gray-900">
    <!-- Banner -->
    <div
      class="h-[300px] w-full bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${refragBackground})` }"
    />

    <!-- Profile Info -->
    <div class="flex px-6 py-[28px] gap-4 items-center">
      <!-- Avatar -->
      <img
        v-if="user?.steam_profile"
        class="w-[72px] h-[72px] rounded-full"
        :src="user?.steam_profile?.avatarfull"
        :alt="$t('profile.header.avatar_alt')"
      />
      <img
        v-else
        class="w-[72px] h-[72px] rounded-full"
        src="../../assets/logos/refrag-icon-button.svg"
        :alt="$t('profile.header.avatar_alt')"
      />

      <!-- Name and Role -->
      <div class="flex flex-col gap-2 justify-center flex-1">
        <!-- Username Display/Edit -->
        <div v-if="!usernameEditing" class="flex gap-2 items-center">
          <h1 class="text-text-primary text-3xl font-semibold">
            {{ getUsername() }}
          </h1>
          <button
            v-if="canEdit"
            @click="$emit('editUsername')"
            class="inline-flex px-2 py-1 mr-1 items-centerborder border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
          >
            <span>{{ $t('common.edit') }}</span>
          </button>
        </div>

        <!-- Username Editing -->
        <div
          v-else
          class="mt-1 text-sm text-text-secondary sm:mt-0 sm:col-span-2 w-1/2 flex items-center"
        >
          <input
            class="block w-full mr-4 rounded-md bg-gray-800 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-text-secondary"
            type="text"
            :value="username"
            @input="$emit('updateUsername', $event.target.value)"
          />
          <button
            @click="$emit('submitUsername')"
            class="inline-flex px-2 py-1 mr-4 items-centerborder border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
          >
            <span>{{ $t('common.save') }}</span>
          </button>
          <button
            @click="$emit('editUsername')"
            class="inline-flex px-2 py-1 items-centerborder border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
          >
            <span>{{ $t('common.cancel') }}</span>
          </button>
        </div>

        <!-- Role -->
        <div :class="roleColor" class="text-sm">{{ role }}</div>
      </div>

      <!-- Friend request -->
      <button
        v-if="friendState"
        @click="handleFriendAction"
        :disabled="friendState.disabled || friendActionPending"
        class="text-xs px-3 py-2.5 rounded-lg flex items-center gap-1.5 duration-300"
        :class="friendState.disabled
          ? 'bg-gray-800 text-text-secondary cursor-default'
          : 'bg-primary-600 text-white hover:bg-primary-500 cursor-pointer'"
      >
        {{ friendActionPending ? "…" : friendState.label }}
      </button>

      <!-- Social Links -->
      <a
        v-if="user?.steam_profile"
        :href="user?.steam_profile?.profileurl"
        target="_blank"
        class="cursor-pointer text-text-primary bg-gray-800 px-3 py-2.5 rounded-lg gap-1 flex items-center hover:bg-gray-700 duration-300"
      >
        <img
          src="../../assets/icons/steam-icon.svg"
          class="h-3 w-3"
          :alt="$t('profile.header.steam_icon_alt')"
        />
        <h1 class="text-xs">Steam</h1>
        <img
          src="../../assets/icons/open-arrow.svg"
          class="h-3 w-3"
          :alt="$t('profile.header.external_link_alt')"
        />
      </a>

      <a
        v-if="user?.faceit_nickname"
        :href="`https://www.faceit.com/en/players/${user?.faceit_nickname}`"
        target="_blank"
        class="cursor-pointer text-text-primary bg-gray-800 px-3 py-2.5 rounded-lg gap-1 flex items-center hover:bg-gray-700 duration-300"
      >
        <img
          src="../../assets/icons/faceit-icon.svg"
          class="h-3 w-3"
          :alt="$t('profile.header.steam_icon_alt')"
        />
        <h1 class="text-xs">Faceit</h1>
        <img
          src="../../assets/icons/open-arrow.svg"
          class="h-3 w-3"
          :alt="$t('profile.header.external_link_alt')"
        />
      </a>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import refragBackground from '../../assets/profile-banner.jpg';

defineEmits(['editUsername', 'submitUsername', 'updateUsername']);
const { t } = useI18n();

function getUsername() {
  return props.user?.username || props.user?.steam_profile?.personaname || t('profile.header.unknown_user');
}

const props = defineProps({
  user: Object,
  usernameEditing: Boolean,
  username: String,
  canEdit: Boolean,
  roleColor: String,
  role: String,
});

// --- Friend request button ---
const auth = useAuth();
const friendships = ref(null); // null until loaded, so the button doesn't flash a wrong state
const incomingRequests = ref([]);
const friendActionPending = ref(false);
const requestSent = ref(false);

const profileUuid = computed(() => props.user?.uuid);

const existingFriendship = computed(
  () => friendships.value?.find((f) => f.friend?.uuid === profileUuid.value) || null,
);
const incomingRequest = computed(
  () => incomingRequests.value.find((r) => r.user?.uuid === profileUuid.value) || null,
);

const friendState = computed(() => {
  if (!profileUuid.value || friendships.value === null) return null;
  if (auth.user()?.uuid === profileUuid.value) return null; // own profile

  if (existingFriendship.value?.status === 'accepted') return { label: t('profile.header.friends'), disabled: true };
  if (incomingRequest.value) return { label: t('profile.header.accept_request'), action: 'accept' };
  if (requestSent.value || existingFriendship.value?.status === 'pending') {
    return { label: t('profile.header.request_sent'), disabled: true };
  }
  return { label: t('profile.header.add_friend'), action: 'add' };
});

function loadFriendships() {
  axios.get('/friendships').then(({ data }) => { friendships.value = data; }).catch(() => {});
  axios.get('/friendships/requests').then(({ data }) => { incomingRequests.value = data; }).catch(() => {});
}

function handleFriendAction() {
  const action = friendState.value?.action;
  if (!action) return;

  friendActionPending.value = true;
  const request = action === 'accept'
    ? axios.post(`/friendships/${incomingRequest.value.id}/accept`)
    : axios.post('/friendships', { user_uuid: profileUuid.value }).then(() => { requestSent.value = true; });

  request
    .then(loadFriendships)
    .catch(() => {})
    .finally(() => { friendActionPending.value = false; });
}

// Same component instance survives profile-to-profile navigation
watch(profileUuid, () => { requestSent.value = false; });

onMounted(loadFriendships);
</script>
